const defaultVertexSource = '\
attribute vec2 vertex;\
attribute vec2 _texCoord;\
varying vec2 texCoord;\
void main() {\
  texCoord = _texCoord;\
  gl_Position = vec4(vertex * 2.0 - 1.0, 0.0, 1.0);\
}';

const defaultFragmentSource = '\
uniform sampler2D texture;\
varying vec2 texCoord;\
void main() {\
  gl_FragColor = texture2D(texture, texCoord);\
}';

export class Shader {
  private vertexAttribute: number | null = null;
  private texCoordAttribute: number | null = null;
  private program: WebGLProgram | null;

  static getDefaultShader(gl: WebGLRenderingContext): Shader {
    // @ts-ignore
    gl.defaultShader = gl.defaultShader || new Shader(gl);
    // @ts-ignore
    return gl.defaultShader;
  }

  constructor(gl: WebGLRenderingContext, vertexSource?: string, fragmentSource?: string) {
    this.program = gl.createProgram();
    if (!this.program) throw new Error('createProgram failed');

    vertexSource = vertexSource || defaultVertexSource;
    fragmentSource = fragmentSource || defaultFragmentSource;
    fragmentSource = 'precision highp float;' + fragmentSource; // annoying requirement is annoying

    gl.attachShader(this.program, compileSource(gl, gl.VERTEX_SHADER, vertexSource));
    gl.attachShader(this.program, compileSource(gl, gl.FRAGMENT_SHADER, fragmentSource));
    gl.linkProgram(this.program);
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      throw new Error(`link error: ${gl.getProgramInfoLog(this.program)}`);
    }
  }

  destroy(gl: WebGLRenderingContext): void {
    if (this.program) {
      gl.deleteProgram(this.program);
      this.program = null;
    }
  }

  uniforms(gl: WebGLRenderingContext, uniforms: Record<string, any>): Shader {
    gl.useProgram(this.program);
    for (const name in uniforms) {
      if (!uniforms.hasOwnProperty(name)) continue;
      const location = gl.getUniformLocation(this.program!, name);
      if (location === null) continue;
      const value = uniforms[name];
      if (Array.isArray(value)) {
        switch (value.length) {
          case 1: gl.uniform1fv(location, new Float32Array(value)); break;
          case 2: gl.uniform2fv(location, new Float32Array(value)); break;
          case 3: gl.uniform3fv(location, new Float32Array(value)); break;
          case 4: gl.uniform4fv(location, new Float32Array(value)); break;
          case 9: gl.uniformMatrix3fv(location, false, new Float32Array(value)); break;
          case 16: gl.uniformMatrix4fv(location, false, new Float32Array(value)); break;
          default: throw new Error(`don't know how to load uniform "${name}" of length ${value.length}`);
        }
      } else if (typeof value === 'number') {
        gl.uniform1f(location, value);
      } else {
        throw new Error(`attempted to set uniform "${name}" to invalid value ${value}`);
      }
    }
    return this;
  }

  drawRect(gl: WebGLRenderingContext, left?: number, top?: number, right?: number, bottom?: number): void {
    if (!gl) throw new Error('WebGL context is not available');
    const viewport = gl.getParameter(gl.VIEWPORT) as Int32Array;
    top = top !== undefined ? (top - viewport[1]) / viewport[3] : 0;
    left = left !== undefined ? (left - viewport[0]) / viewport[2] : 0;
    right = right !== undefined ? (right - viewport[0]) / viewport[2] : 1;
    bottom = bottom !== undefined ? (bottom - viewport[1]) / viewport[3] : 1;

    // @ts-ignore
    if (!gl.vertexBuffer) {
      // @ts-ignore
      gl.vertexBuffer = gl.createBuffer();
    }
    // @ts-ignore
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([left, top, left, bottom, right, top, right, bottom]), gl.STATIC_DRAW);

    // @ts-ignore
    if (!gl.texCoordBuffer) {
      // @ts-ignore
      gl.texCoordBuffer = gl.createBuffer();
      // @ts-ignore
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.texCoordBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 0, 1, 1, 0, 1, 1]), gl.STATIC_DRAW);
    }

    if (this.vertexAttribute === null) {
      this.vertexAttribute = gl.getAttribLocation(this.program!, 'vertex');
      gl.enableVertexAttribArray(this.vertexAttribute);
    }
    if (this.texCoordAttribute === null) {
      this.texCoordAttribute = gl.getAttribLocation(this.program!, '_texCoord');
      gl.enableVertexAttribArray(this.texCoordAttribute);
    }

    gl.useProgram(this.program);
    // @ts-ignore
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.vertexBuffer);
    gl.vertexAttribPointer(this.vertexAttribute, 2, gl.FLOAT, false, 0, 0);
    // @ts-ignore
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.texCoordBuffer);
    gl.vertexAttribPointer(this.texCoordAttribute, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}

function compileSource(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(`compile error: ${gl.getShaderInfoLog(shader)}`);
  }
  return shader;
}

export function warpShader(gl: WebGLRenderingContext, uniforms: any, warp: any): Shader {
  return new Shader(gl, undefined, `${uniforms}
    uniform sampler2D texture;
    uniform vec2 texSize;
    varying vec2 texCoord;
    void main() {
      vec2 coord = texCoord * texSize;
      ${warp}
      gl_FragColor = texture2D(texture, coord / texSize);
      vec2 clampedCoord = clamp(coord, vec2(0.0), texSize);
      if (coord != clampedCoord) {
        /* fade to transparent if we are outside the image */
        gl_FragColor.a *= max(0.0, 1.0 - length(coord - clampedCoord));
      }
    }`
  );
}
